import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Container,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { authApi } from '../api/auth';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch {
      setError(t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F5F7FB',
        padding: '20px',
      }}
    >
      <Container size="sm" w="100%">
        <Paper radius="md" p="xl" withBorder>
          <Group justify="center" mb="lg">
            <Image
              src="/strassenwaechter-logo.png"
              alt="Straßenwächter e.V."
              height={80}
              fit="contain"
            />
          </Group>

          <Title order={2} ta="center" mb="sm">
            {t('forgotPassword.title')}
          </Title>

          <Text c="dimmed" ta="center" mb="lg" size="sm">
            {t('forgotPassword.subtitle')}
          </Text>

          {submitted ? (
            <Alert icon={<IconCheck />} color="green">
              {t('forgotPassword.success')}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {error && (
                  <Alert icon={<IconAlertCircle />} color="red">
                    {error}
                  </Alert>
                )}

                <TextInput
                  label={t('forgotPassword.email')}
                  type="email"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                  required
                  disabled={loading}
                />

                <Button type="submit" fullWidth loading={loading}>
                  {t('forgotPassword.submit')}
                </Button>
              </Stack>
            </form>
          )}

          <Text ta="center" mt="lg" size="sm">
            <Link to="/" style={{ color: '#1971C2', textDecoration: 'none' }}>
              {t('forgotPassword.backToLogin')}
            </Link>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
